import { useMemo } from 'react';
import { Heart } from 'lucide-react';
import { Card, CardContent } from '~/components/ui/card';
import { StepHeader } from '~/components/forms/StepHeader';
import { SignatureField } from '~/components/forms/SignatureField';
import { StudentMedicalChunk } from '~/components/form-chunks/StudentMedicalChunk';

interface AppStudentMedicalStepProps {
  formData: any;
  updateFormData: (path: string, value: any) => void;
  onReviewComplete?: (reviewed: boolean) => void;
}

export function AppStudentMedicalStep({ formData, updateFormData, onReviewComplete }: AppStudentMedicalStepProps) {
  const students = useMemo(() => {
    const persisted = (formData?.students || []).filter((s: any) => s.firstName || s.lastName);
    const newOnes = (formData?.newStudents || []).filter((s: any) => s.firstName || s.lastName);
    return [...persisted, ...newOnes].map((s: any) => ({
      id: s.studentEnrollmentId || s.tempId,
      firstName: s.firstName,
      lastName: s.lastName,
      type: 'new' as const,
    }));
  }, [formData?.students, formData?.newStudents]);

  const medicalInfo = formData?.studentMedicalInfo || {};

  const handleUpdate = (updatedMedicalInfo: Record<string, any>) => {
    updateFormData('studentMedicalInfo', updatedMedicalInfo);
  };

  if (students.length === 0) {
    return (
      <div className="space-y-6">
        <StepHeader icon={Heart} title="Medical Information" />
        <Card className="max-w-xl mx-auto">
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              No students added yet. Please go back and add at least one student.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StepHeader
        icon={Heart}
        title="Medical Information"
        description="Please provide medical information for each student."
      />

      <div className="max-w-2xl mx-auto">
        <StudentMedicalChunk
          students={students}
          medicalInfo={medicalInfo}
          onUpdate={handleUpdate}
          onReviewComplete={onReviewComplete}
          showOtcMedications={true}
        />

        {/* Medical Consent Signature */}
        <SignatureField
          value={formData?.medicalConsentSignature || ''}
          onChange={(value) => updateFormData('medicalConsentSignature', value)}
          label="Medical Consent Signature"
          description="By signing below, you authorize Immanuel Classical to administer the medications you have approved above to your child(ren) in case of illness."
          consentText="I hereby give permission for the adult staff or teachers of Immanuel Classical to administer the over-the-counter medications I have selected above to my child(ren) when needed. I understand that I can update these preferences at any time by contacting the school office."
          id="medical-consent-signature"
          required
        />
      </div>
    </div>
  );
}
